/**
 * OrderConfirmed.tsx — "Order Received" thank you page
 * URL: /shop/:businessId/order-confirmed
 */
import React from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import { hasCompleteBankDetails, parseBankDetails, normalizeBankDetails, type BankDetails } from '../../utils/bankDetails';

interface ConfirmedState {
  orderNumber: string;
  paymentMethod: 'Bank Transfer' | 'Cash on Collection' | 'Part Payment';
  customerName: string;
  customerEmail: string;
  total: number;
  depositAmount?: number;
  bankDetails?: BankDetails | null;
  businessEmail?: string | null;
  businessPhone?: string | null;
}

function BankTransferInstructions({
  total,
  orderNumber,
  bank,
  businessEmail,
  businessPhone,
}: {
  total: number;
  orderNumber: string;
  bank?: BankDetails | null;
  businessEmail?: string | null;
  businessPhone?: string | null;
}) {
  if (!hasCompleteBankDetails(bank)) {
    return (
      <>
        <p style={{ margin: '0 0 8px', fontSize: 14, color: '#92400e' }}>
          Please transfer <strong>£{Number(total).toFixed(2)}</strong> by bank transfer.
        </p>
        <p style={{ margin: 0, fontSize: 13, color: '#b45309' }}>
          Please contact us for payment instructions
          {businessEmail ? ` at ${businessEmail}` : ''}
          {businessPhone ? `${businessEmail ? ' or' : ' on'} ${businessPhone}` : ''}.
        </p>
      </>
    );
  }

  return (
    <>
      <p style={{ margin: '0 0 10px', fontSize: 14, color: '#1e40af' }}>
        Please transfer <strong>£{Number(total).toFixed(2)}</strong> using the details below.
      </p>
      <div style={{ fontSize: 13, color: '#1e3a8a', lineHeight: 1.7 }}>
        <div><span style={{ color: '#6b7280' }}>Bank Name:</span> <strong>{bank!.bank_name}</strong></div>
        <div><span style={{ color: '#6b7280' }}>Account Name:</span> <strong>{bank!.bank_account_name || bank!.bank_name}</strong></div>
        <div><span style={{ color: '#6b7280' }}>Sort Code:</span> <strong>{bank!.bank_sort_code}</strong></div>
        <div><span style={{ color: '#6b7280' }}>Account Number:</span> <strong>{bank!.bank_account_number}</strong></div>
      </div>
      <p style={{ margin: '10px 0 0', fontSize: 13, color: '#92400e' }}>
        ⚠️ <strong>Payment Reference:</strong> {orderNumber}
      </p>
      <p style={{ margin: '8px 0 0', fontSize: 13, color: '#3b82f6' }}>
        Your order will be confirmed once payment is received.
      </p>
    </>
  );
}

const OrderConfirmed: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const location = useLocation();
  const state = (location.state || {}) as Partial<ConfirmedState>;

  const {
    orderNumber = '—',
    paymentMethod = 'Bank Transfer',
    customerName = 'Customer',
    customerEmail = '',
    total = 0,
    depositAmount,
    bankDetails,
    businessEmail,
    businessPhone,
  } = state;

  const bank = normalizeBankDetails(parseBankDetails(bankDetails || {}));

  const paymentConfig: Record<string, { icon: string; colour: string; bg: string; border: string; heading: string; body: React.ReactNode }> = {
    'Bank Transfer': {
      icon: '🏦',
      colour: '#1d4ed8',
      bg: '#eff6ff',
      border: '#bfdbfe',
      heading: 'Bank Transfer',
      body: (
        <BankTransferInstructions
          total={total}
          orderNumber={orderNumber}
          bank={bank}
          businessEmail={businessEmail}
          businessPhone={businessPhone}
        />
      ),
    },
    'Cash on Collection': {
      icon: '💵',
      colour: '#166534',
      bg: '#f0fdf4',
      border: '#bbf7d0',
      heading: 'Cash on Collection',
      body: (
        <>
          <p style={{ margin: '0 0 8px', fontSize: 14, color: '#166534' }}>
            You will pay <strong>£{Number(total).toFixed(2)}</strong> in cash when you collect your order.
          </p>
          <p style={{ margin: 0, fontSize: 13, color: '#16a34a' }}>
            We will be in touch to arrange a collection time.
          </p>
        </>
      ),
    },
    'Part Payment': {
      icon: '💰',
      colour: '#92400e',
      bg: '#fefce8',
      border: '#fde68a',
      heading: 'Part Payment / Deposit',
      body: (
        <>
          <p style={{ margin: '0 0 8px', fontSize: 14, color: '#92400e' }}>
            Deposit of <strong>£{Number(depositAmount || 0).toFixed(2)}</strong> noted.
          </p>
          <p style={{ margin: '0 0 8px', fontSize: 14, color: '#92400e' }}>
            Remaining balance: <strong>£{(Number(total) - Number(depositAmount || 0)).toFixed(2)}</strong>
          </p>
          <p style={{ margin: 0, fontSize: 13, color: '#b45309' }}>
            We will contact you to confirm the payment arrangement.
          </p>
        </>
      ),
    },
  };

  const cfg = paymentConfig[paymentMethod] || paymentConfig['Bank Transfer'];

  const steps: { icon: string; text: string }[] = [
    { icon: '✅', text: 'Your order has been saved in our system' },
    { icon: '📧', text: `A confirmation email has been sent to ${customerEmail}` },
    (paymentMethod === 'Bank Transfer')
      ? { icon: '🏦', text: hasCompleteBankDetails(bank) ? 'Use the bank details above (also in your email) to complete payment' : 'Check your email or contact us for payment instructions' }
      : paymentMethod === 'Cash on Collection'
      ? { icon: '📦', text: 'We will contact you to arrange a collection time' }
      : { icon: '📞', text: 'We will contact you to arrange the deposit payment' },
    { icon: '🎉', text: 'Once confirmed, we will process and prepare your order' },
  ];

  const contactLine = [businessEmail, businessPhone].filter(Boolean).join(' · ');

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4ff', fontFamily: 'system-ui, sans-serif', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>
      <div style={{ width: '100%', maxWidth: 540 }}>
        <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 4px 32px rgba(0,0,0,0.10)', overflow: 'hidden' }}>
          <div style={{ background: 'linear-gradient(135deg, #166534, #16a34a)', padding: '36px 32px', textAlign: 'center', color: '#fff' }}>
            <div style={{ fontSize: 60, marginBottom: 10 }}>🎉</div>
            <h1 style={{ margin: '0 0 6px', fontSize: 26, fontWeight: 900 }}>Order Received!</h1>
            <p style={{ margin: 0, fontSize: 15, opacity: 0.85 }}>Thank you, {customerName}</p>
          </div>

          <div style={{ padding: '28px 32px' }}>
            <div style={{ textAlign: 'center', marginBottom: 22 }}>
              <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Order Number</span>
              <div style={{ fontSize: 22, fontWeight: 900, color: '#111827', marginTop: 4, letterSpacing: 1 }}>{orderNumber}</div>
            </div>

            <div style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 10, padding: '14px 18px', marginBottom: 22 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: cfg.colour, marginBottom: 8 }}>
                {cfg.icon} Payment method: {cfg.heading}
              </div>
              {cfg.body}
            </div>

            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 12px' }}>What Happens Next</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {steps.map((step, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: '#374151' }}>
                    <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{step.icon}</span>
                    <span>{step.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#6b7280', textAlign: 'center', marginBottom: 22 }}>
              🔒 Your order details are safe. We will be in touch — usually within 1 business day.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Link
                to={`/shop/${businessId}`}
                style={{ display: 'block', textAlign: 'center', padding: '13px 0', borderRadius: 9, background: '#1e3a8a', color: '#fff', fontWeight: 800, fontSize: 15, textDecoration: 'none' }}
              >
                🛍️ Continue Shopping
              </Link>
              <Link
                to={`/shop/${businessId}/track?email=${encodeURIComponent(customerEmail)}&order=${encodeURIComponent(orderNumber)}`}
                style={{ display: 'block', textAlign: 'center', padding: '11px 0', borderRadius: 9, background: '#f9fafb', border: '1.5px solid #e5e7eb', color: '#374151', fontWeight: 700, fontSize: 14, textDecoration: 'none' }}
              >
                📦 Track this order
              </Link>
              <Link
                to={`/shop/${businessId}/dashboard`}
                style={{ display: 'block', textAlign: 'center', padding: '11px 0', borderRadius: 9, background: '#fff', border: '1.5px solid #e5e7eb', color: '#6b7280', fontWeight: 600, fontSize: 13, textDecoration: 'none' }}
              >
                Account holders: view all orders
              </Link>
            </div>
          </div>
        </div>

        {contactLine ? (
          <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: '#9ca3af' }}>
            Need help? Contact us{contactLine ? `: ${contactLine}` : ''}.
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default OrderConfirmed;
